<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import MediaRenderer from '@/components/MediaRenderer.vue'
import UiCta from '@/components/common/UiCta.vue'
import UiIcon from '@/components/common/UiIcon.vue'
import type { CtaOptions } from '@/composables/useCta'
import type { HeroCta } from '@/models/hero'
import { PlausibleGoalsService } from '@/services/plausible-goals.service'
import { useContentStore } from '@/stores/content.store'
import { useFrontpageStore } from '@/stores/frontpage.store'
import { useHeroStore } from '@/stores/hero.store'
import { formatInteger } from '@/utils/number'

const route = useRoute()
const { locale } = useI18n()

const contentStore = useContentStore()
const frontpageStore = useFrontpageStore()
const heroStore = useHeroStore()

const { isLoading: isContentLoading, loading: contentLoading } = storeToRefs(contentStore)
const { isLoading: isFrontpageLoading, loading: frontpageLoading } = storeToRefs(frontpageStore)
const { data } = storeToRefs(heroStore)

const hero = computed(() => data.value?.hero)
const media = computed(() => hero.value?.media)
const routeBaseName = computed(() => String(route.name ?? 'home').replace(/-(en|no)$/, ''))
const effectiveHeroHeight = computed(() => cleanString(hero.value?.height) ?? 'full')
const isSuperimposed = computed(() => effectiveHeroHeight.value === 'full')

function cleanString(value?: string) {
  if (typeof value !== 'string') return undefined

  const trimmed = value.trim()
  return trimmed || undefined
}

function normalizeScrollTarget(value?: string) {
  const trimmed = cleanString(value)
  if (!trimmed) return undefined

  const escaped = typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
    ? CSS.escape(trimmed)
    : trimmed.replace(/\\/g, '\\\\').replace(/"/g, '\\"')

  return `[data-uuid="${escaped}"]`
}

function serializeCtaTarget(item: CtaOptions) {
  if (typeof item.to === 'string') return item.to
  if (typeof item.targetEl === 'string') return item.targetEl
  return ''
}

const isAwaitingApiResponse = computed(() => {
  if (routeBaseName.value === 'home') {
    return (
      isContentLoading.value ||
      !!contentLoading.value.pages ||
      !contentStore.hasLoaded('pages') ||
      isFrontpageLoading.value ||
      !!frontpageLoading.value.frontpage
    )
  }

  if (routeBaseName.value === 'page') {
    return (
      isContentLoading.value ||
      !!contentLoading.value.pages ||
      !contentStore.hasLoaded('pages')
    )
  }

  if (routeBaseName.value === 'post') {
    return (
      isContentLoading.value ||
      !!contentLoading.value.posts ||
      !contentStore.hasLoaded('posts')
    )
  }

  return false
})

const sectionClasses = computed(() => [
  'hero',
  'relative',
  'z-0',
  isSuperimposed.value ? 'superimposed' : '',
  hasHeroMedia.value ? 'has-image' : 'no-image',
  hasHeroMedia.value ? '' : 'bg-gradient-hero',
])

const supertitleHtml = computed(() => hero.value?.supertitle ?? '')
const titleHtml = computed(() => hero.value?.title ?? '')
const leadHtml = computed(() => hero.value?.content_html ?? hero.value?.content ?? '')
const disclaimerHtml = computed(() => hero.value?.disclaimer ?? '')

const hasSupertitle = computed(() => !!supertitleHtml.value.trim())
const hasTitle = computed(() => !!titleHtml.value.trim())
const hasLead = computed(() => !!leadHtml.value.trim())
const hasDisclaimer = computed(() => !!disclaimerHtml.value.trim())
const supertitleIcon = computed(() => cleanString(hero.value?.icon) ?? 'sparkles')

const highlightItems = computed(() =>
  (hero.value?.highlights ?? [])
    .map((item) => ({
      icon: cleanString(item.icon),
      title: cleanString(item.title),
    }))
    .filter((item) => item.title),
)

function createCtaOptions(item: HeroCta, index: number): CtaOptions | null {
  const text = cleanString(item.label)
  if (!text || !item.type) return null

  const shared = {
    text,
    icon: cleanString(item.icon),
    iconPosition: item.icon_position ?? 'right',
    appearance: item.appearance ?? 'button',
    color: index === 0 ? 'primary' as const : 'secondary' as const,
  }

  if (item.type === 'internal') {
    const to = cleanString(item.url)
    if (!to) return null

    return {
      ...shared,
      type: 'internal',
      to,
    }
  }

  if (item.type === 'external') {
    const to = cleanString(item.external_url)
    if (!to) return null

    return {
      ...shared,
      type: 'external',
      to,
    }
  }

  if (item.type === 'scroll_to') {
    const targetEl = normalizeScrollTarget(item.scroll_target_uuid)
    if (!targetEl) return null

    return {
      ...shared,
      type: 'scroll',
      targetEl,
    }
  }

  return null
}

const ctaItems = computed<CtaOptions[]>(() =>
  (hero.value?.ctas ?? [])
    .map(createCtaOptions)
    .filter((item): item is CtaOptions => item !== null),
)

const mediaType = computed(() => media.value?.type ?? 'none')
const isImageVisual = computed(() => mediaType.value === 'image' && Boolean(media.value?.has_media))
const isVideoVisual = computed(() => mediaType.value === 'video' && Boolean(media.value?.has_media))
const isIllustrationVisual = computed(() => mediaType.value === 'illustration')

const imageId = computed(() => {
  const item = media.value
  if (!isImageVisual.value || !item) return undefined
  return item.image_id && item.image_id > 0 ? item.image_id : undefined
})

const imageUrl = computed(() => {
  const item = media.value
  if (!isImageVisual.value || !item) return ''
  return cleanString(item.image_url) ?? ''
})

const imageAlt = computed(() => cleanString(media.value?.image_alt) ?? '')

const videoFileUrl = computed(() => {
  const item = media.value
  if (!isVideoVisual.value || !item) return ''

  return (
    cleanString(item.video?.file_url) ||
    cleanString(item.video?.url) ||
    ''
  )
})

const videoEmbedUrl = computed(() => {
  const item = media.value
  if (!isVideoVisual.value || !item) return ''
  return cleanString(item.video?.embed_url) ?? ''
})

const hasHeroMedia = computed(() =>
  !!imageId.value ||
  !!imageUrl.value ||
  !!videoFileUrl.value ||
  !!videoEmbedUrl.value,
)
const shoutBoxLabel = computed(() => media.value?.shout_box?.top ?? '')
const shoutBoxValue = computed(() => media.value?.shout_box?.content ?? '')
const shoutBoxDescription = computed(() => media.value?.shout_box?.bottom ?? '')
const hasShoutBoxContent = computed(() =>
  Boolean(
    shoutBoxLabel.value.trim() ||
    shoutBoxValue.value.trim() ||
    shoutBoxDescription.value.trim(),
  ),
)
const showShoutBox = computed(() =>
  !isAwaitingApiResponse.value &&
  media.value?.has_shout_box === true &&
  mediaType.value !== 'none' &&
  hasShoutBoxContent.value,
)
const inlineShoutBoxTitle = computed(() => cleanString(hero.value?.shout_box?.title) ?? '')
const inlineShoutBoxType = computed(() => cleanString(hero.value?.shout_box?.type) ?? 'default')
const isInlineShoutBoxAnalytics = computed(() => inlineShoutBoxType.value === 'analytics')
const analyticsFormSubmissionTotal = ref<number | null>(null)
const isLoadingAnalyticsFormSubmissionTotal = ref(false)
const analyticsFormSubmissionTotalText = computed(() => {
  if (analyticsFormSubmissionTotal.value === null) return ''

  return formatInteger(analyticsFormSubmissionTotal.value, locale.value)
})
const analyticsFormSubmissionTotalPlaceholder = computed(() =>
  formatInteger(99_999, locale.value),
)
const showInlineShoutBox = computed(() =>
  !isAwaitingApiResponse.value &&
  hero.value?.shout_box?.show === true &&
  inlineShoutBoxTitle.value.length > 0,
)

watch(
  () => showInlineShoutBox.value && isInlineShoutBoxAnalytics.value,
  async (shouldFetch) => {
    if (!shouldFetch || analyticsFormSubmissionTotal.value !== null) return

    isLoadingAnalyticsFormSubmissionTotal.value = true

    try {
      const totals = await PlausibleGoalsService.formSubmissionTotals()
      analyticsFormSubmissionTotal.value = totals.totalFormSubmissions
    } catch (error) {
      console.warn('Unable to fetch Plausible form submission totals.', error)
    } finally {
      isLoadingAnalyticsFormSubmissionTotal.value = false
    }
  },
  { immediate: true },
)

const heroIdentityKey = computed(() => String(data.value?.id ?? data.value?.slug ?? 'hero'))
const heroMediaKey = computed(() => [
  heroIdentityKey.value,
  mediaType.value,
  imageId.value ?? '',
  imageUrl.value,
  videoFileUrl.value,
  videoEmbedUrl.value,
].join('::'))

const heroCopyKey = computed(() => [
  heroIdentityKey.value,
  supertitleHtml.value,
  titleHtml.value,
  leadHtml.value,
  disclaimerHtml.value,
  highlightItems.value.map((item) => `${item.icon ?? ''}:${item.title ?? ''}`).join('|'),
  ctaItems.value.map((item) => `${item.type}:${item.text}:${serializeCtaTarget(item)}`).join('|'),
].join('::'))

const showHeroSection = computed(() => isAwaitingApiResponse.value || !!hero.value)
const showHeroCopy = computed(() => {
  if (isAwaitingApiResponse.value) return false

  return (
    hasSupertitle.value ||
    hasTitle.value ||
    hasLead.value ||
    hasDisclaimer.value ||
    highlightItems.value.length > 0 ||
    ctaItems.value.length > 0
  )
})

const showIllustration = computed(() =>
  !isAwaitingApiResponse.value &&
  isIllustrationVisual.value &&
  !hasHeroMedia.value,
)
const heroInnerClasses = computed(() => [
  'container',
  'hero-inner',
  showIllustration.value ? 'hero-inner--split' : '',
])
const heroCopyClasses = computed(() => [
  'hero-copy-block',
  showIllustration.value ? 'hero-text-block' : '',
  mediaType.value === 'none' ? 'hero-copy-block--no-media' : '',
])
</script>

<template>
  <section
    v-if="showHeroSection"
    itemscope
    itemprop="primaryImageOfPage"
    :data-id="data?.id"
    :data-type="data?.type"
    :data-layout="hero?.layout"
    :data-height="effectiveHeroHeight"
    :data-media-type="mediaType"
    :data-size="hero?.size"
    :data-width="hero?.width"
    :class="sectionClasses"
  >
    <Transition name="hero-media" appear>
      <img
        v-if="imageUrl"
        :key="heroMediaKey"
        :src="imageUrl"
        :alt="imageAlt"
        class="hero-image absolute inset-0 -z-10 h-full w-full object-cover"
        loading="eager"
        decoding="async"
      />

      <MediaRenderer
        v-else-if="imageId"
        :key="heroMediaKey"
        :id="imageId"
        image-size="hero"
        loading="eager"
        class="hero-image absolute inset-0 -z-10"
      />

      <video
        v-else-if="videoFileUrl"
        :key="heroMediaKey"
        class="hero-image absolute inset-0 -z-10 h-full w-full object-cover"
        autoplay
        muted
        loop
        playsinline
        preload="metadata"
      >
        <source :src="videoFileUrl" />
      </video>

      <iframe
        v-else-if="videoEmbedUrl"
        :key="heroMediaKey"
        :src="videoEmbedUrl"
        class="hero-image absolute inset-0 -z-10 h-full w-full"
        title="Hero media"
        loading="eager"
        allow="autoplay; encrypted-media; picture-in-picture"
        allowfullscreen
      />

    </Transition>

    <div :class="heroInnerClasses">
      <Transition
        :name="showIllustration ? undefined : 'hero-copy'"
        mode="out-in"
        :appear="!showIllustration"
      >
        <div
          v-if="showHeroCopy"
          :key="heroCopyKey"
          :class="heroCopyClasses"
        >
          <header class="hero-header flex flex-col gap-7">
            <div
              v-if="hasSupertitle"
              class="inline-flex w-fit items-center gap-2 rounded-full border border-primary/30 bg-primary-soft px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary-deep"
            >
            <UiIcon
              :name="supertitleIcon"
              class="size-4"
            />

              <span v-html="supertitleHtml" />
            </div>

            <h1
              v-if="hasTitle"
              itemprop="headline"
              class="font-display text-4xl font-semibold leading-[1.05] tracking-tight text-balance sm:text-5xl md:text-6xl"
              v-html="titleHtml"
            />

            <div
              v-if="hasLead"
              itemprop="text"
              class="max-w-xl text-lg leading-relaxed text-muted-foreground text-pretty"
              v-html="leadHtml"
            />
          </header>

          <ul
            v-if="highlightItems.length"
            class="hero-highlights max-w-xl grid gap-2.5 text-sm sm:grid-cols-2"
          >
            <li
              v-for="(item, index) in highlightItems"
              :key="`${item.title}-${index}`"
              class="flex items-center gap-2.5 text-foreground/85"
            >
              <span class="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary-deep">
              <UiIcon
                :name="item.icon ?? 'sparkles'"
                class="size-4"
              />
              </span>

              <span>{{ item.title }}</span>
            </li>
          </ul>

          <div
            v-if="ctaItems.length || showInlineShoutBox"
            class="utility-wrapper flex flex-wrap items-center gap-3"
          >
            <div
              v-if="ctaItems.length"
              class="cta-wrapper items-center"
            >
              <div class="flex flex-wrap gap-3">
                <UiCta
                  v-for="(item, index) in ctaItems"
                  :key="`${item.type}-${item.text}-${index}`"
                  :options="item"
                />
              </div>
            </div>

            <div
              v-if="showInlineShoutBox"
              class="hero-inline-shout-box"
              :data-type="inlineShoutBoxType"
              :data-loading="isLoadingAnalyticsFormSubmissionTotal"
            >
              <UiIcon
                v-if="isInlineShoutBoxAnalytics"
                name="Users"
                class="size-8 shrink-0"
              />
              <p>
                <strong
                  v-if="isInlineShoutBoxAnalytics"
                  class="hero-inline-shout-box__value"
                  :class="analyticsFormSubmissionTotalText ? '' : 'opacity-0'"
                  aria-hidden="true"
                >
                  {{ analyticsFormSubmissionTotalText || analyticsFormSubmissionTotalPlaceholder }}
                </strong>
                <span>{{ inlineShoutBoxTitle }}</span>
              </p>
            </div>
          </div>

          <p
            v-if="hasDisclaimer"
            class="text-xs text-muted-foreground"
            v-html="disclaimerHtml"
          >
          </p>
        </div>
      </Transition>

      <div
        v-if="showIllustration"
        class="relative"
        aria-hidden="true"
      >
        <div class="relative aspect-[5/4] overflow-hidden rounded-xl border border-border bg-card">
          <svg
            viewBox="0 0 1280 1024"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="h-full w-full"
            preserveAspectRatio="xMidYMid slice"
          >
            <rect width="1280" height="1024" fill="var(--background)" />
            <path
              d="M0,920 C220,820 420,720 700,760 C920,790 1080,860 1280,800 L1280,1024 L0,1024 Z"
              fill="var(--primary)"
              opacity="0.12"
            />
            <path
              d="M0,980 C200,880 380,840 560,890 C760,945 940,1000 1280,920 L1280,1024 L0,1024 Z"
              fill="var(--primary)"
              opacity="0.22"
            />
            <circle cx="260" cy="500" r="4" fill="var(--primary)" opacity="0.55" class="dot" style="animation-delay:0s" />
            <circle cx="400" cy="490" r="11" fill="var(--primary)" opacity="0.55" class="dot" style="animation-delay:0.6s" />
            <circle cx="160" cy="575" r="13" fill="var(--primary)" opacity="0.55" class="dot" style="animation-delay:1.2s" />
            <circle cx="290" cy="625" r="5" fill="var(--primary)" opacity="0.55" class="dot" style="animation-delay:0.4s" />
            <circle cx="345" cy="695" r="8" fill="var(--primary)" opacity="0.55" class="dot" style="animation-delay:1.6s" />
            <circle cx="990" cy="215" r="7" fill="var(--primary)" opacity="0.55" class="dot" style="animation-delay:0.3s" />
            <circle cx="1170" cy="260" r="13" fill="var(--primary)" opacity="0.55" class="dot" style="animation-delay:0.9s" />
            <circle cx="1075" cy="325" r="5" fill="var(--primary)" opacity="0.55" class="dot" style="animation-delay:1.5s" />
            <circle cx="1100" cy="385" r="9" fill="var(--primary)" opacity="0.55" class="dot" style="animation-delay:0.2s" />
            <circle cx="945" cy="425" r="14" fill="var(--primary)" opacity="0.55" class="dot" style="animation-delay:1.1s" />
            <circle cx="935" cy="580" r="12" fill="var(--primary)" opacity="0.55" class="dot" style="animation-delay:0.7s" />
          </svg>
        </div>

      </div>

      <div
        v-if="showShoutBox"
        class="hero-shout-box hidden w-fit rounded-xl border border-border bg-card p-4 sm:block"
        :class="showIllustration ? 'absolute -bottom-6 -left-2 sm:-left-6' : 'mt-8'"
      >
        <div
          v-if="shoutBoxLabel"
          class="text-xs uppercase tracking-wider text-muted-foreground"
        >
          {{ shoutBoxLabel }}
        </div>
        <div
          v-if="shoutBoxValue"
          class="mt-1 font-display text-2xl font-semibold"
        >
          {{ shoutBoxValue }}
        </div>
        <div
          v-if="shoutBoxDescription"
          class="text-xs text-muted-foreground"
        >
          {{ shoutBoxDescription }}
        </div>
      </div>
    </div>
  </section>
</template>
